import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RTL_LANGUAGES } from "~/config/constants";
import { Toaster } from "sonner";
import { ErrorBoundary } from "~/components/shared/error-boundary";
import { PwaUpdatePrompt } from "~/components/shared/pwa-update-prompt";
import { AuthGuard, GuestGuard } from "~/components/shared/auth-guard";
import { AppShell } from "~/components/layout/app-shell";
import { useAuthInit } from "~/lib/hooks/use-auth-init";

const LoginPage = lazy(() => import("~/routes/login"));
const RegisterPage = lazy(() => import("~/routes/register"));
const DashboardPage = lazy(() => import("~/routes/dashboard"));
const OrdersListPage = lazy(() => import("~/routes/orders/index"));
const OrderDetailPage = lazy(() => import("~/routes/orders/order-detail"));
const OrderHistoryPage = lazy(() => import("~/routes/orders/history"));
const ReviewsPage = lazy(() => import("~/routes/reviews"));
const MenuOverviewPage = lazy(() => import("~/routes/menu/index"));
const CategoryFormPage = lazy(() => import("~/routes/menu/category-form"));
const ItemFormPage = lazy(() => import("~/routes/menu/item-form"));
const ProfilePage = lazy(() => import("~/routes/profile"));
const AppPreviewPage = lazy(() => import("~/routes/app-preview"));
const SettingsPage = lazy(() => import("~/routes/settings"));
const CityConfigsPage = lazy(() => import("~/routes/city-configs"));
const PendingApprovalPage = lazy(() => import("~/routes/pending-approval"));
const BlockedPage = lazy(() => import("~/routes/blocked"));
const NotFoundPage = lazy(() => import("~/routes/not-found"));

function PageLoader() {
  return (
    <div className="flex h-32 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function SuspenseRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <GuestGuard>
        <SuspenseRoute>
          <LoginPage />
        </SuspenseRoute>
      </GuestGuard>
    ),
  },
  {
    path: "/register",
    element: (
      <GuestGuard>
        <SuspenseRoute>
          <RegisterPage />
        </SuspenseRoute>
      </GuestGuard>
    ),
  },
  {
    path: "/pending-approval",
    element: (
      <SuspenseRoute>
        <PendingApprovalPage />
      </SuspenseRoute>
    ),
  },
  {
    path: "/blocked",
    element: (
      <SuspenseRoute>
        <BlockedPage />
      </SuspenseRoute>
    ),
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseRoute>
            <DashboardPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "orders",
        element: (
          <SuspenseRoute>
            <OrdersListPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "orders/history",
        element: (
          <SuspenseRoute>
            <OrderHistoryPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "orders/:id",
        element: (
          <SuspenseRoute>
            <OrderDetailPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "reviews",
        element: (
          <SuspenseRoute>
            <ReviewsPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "menu",
        element: (
          <SuspenseRoute>
            <MenuOverviewPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "menu/categories/new",
        element: (
          <SuspenseRoute>
            <CategoryFormPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "menu/categories/:id",
        element: (
          <SuspenseRoute>
            <CategoryFormPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "menu/items/new",
        element: (
          <SuspenseRoute>
            <ItemFormPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "menu/items/:id",
        element: (
          <SuspenseRoute>
            <ItemFormPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "profile",
        element: (
          <SuspenseRoute>
            <ProfilePage />
          </SuspenseRoute>
        ),
      },
      {
        path: "app-preview",
        element: (
          <SuspenseRoute>
            <AppPreviewPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "city-configs",
        element: (
          <SuspenseRoute>
            <CityConfigsPage />
          </SuspenseRoute>
        ),
      },
      {
        path: "settings",
        element: (
          <SuspenseRoute>
            <SettingsPage />
          </SuspenseRoute>
        ),
      },
    ],
  },
  {
    path: "*",
    element: (
      <SuspenseRoute>
        <NotFoundPage />
      </SuspenseRoute>
    ),
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      networkMode: "online",
    },
  },
});

function DirectionManager() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const isRtl = RTL_LANGUAGES.includes(
      i18n.language as (typeof RTL_LANGUAGES)[number],
    );
    document.documentElement.dir = isRtl ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return null;
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
  useAuthInit();
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthInitializer>
          <DirectionManager />
          <RouterProvider router={router} />
          <Toaster position="bottom-center" />
          <PwaUpdatePrompt />
        </AuthInitializer>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
